import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Inheritance } from './inheritance'

/**
 * From the perspective of a given entity, these labels can be used to refer to
 * certain related entities, such as the
 * {@link Relation.Parent parent} of the given entity
 *
 * | Relation | Description |
 * | :-- | :-- |
 * | `parent` | Entity immediately above the given entity in the given entity's dimension |
 * | `child` | Entity immediately below the given entity in the given entity's dimension |
 * | `root` | Top entity in the hierarchy of a given dimension (or the dimension of a given entity) |
 * | `leaf` | Bottom entity in the hierarchy of a given dimension (or the dimension of a given entity) |
 * | `ancestors` | All entities above the given entity in the given entity's dimension |
 * | `descendants` | All entities below the given entity in the given entity's dimension |
 */
export type Relation =
  | 'parent'
  | 'child'
  | 'root'
  | 'leaf'
  | 'ancestors'
  | 'descendants'
export namespace Relation {
  export const ALL: readonly Relation[] = [
    'parent',
    'child',
    'root',
    'leaf',
    'ancestors',
    'descendants',
  ]
  export const isRelation = TypeChecker.Factory.oneOf(ALL)
  export const assertIsRelation: TypeAsserter<Relation> =
    TypeAsserter.Factory.from(isRelation)

  /**
   * Used to produce {@link Where}
   */
  type WhereHelper<
    TCriteria extends Where.Criteria,
    K extends keyof Where.Criteria,
    V extends Where.Criteria[K],
    TRelation extends Relation,
    // NOTE: this should never be set externally. We just do this here so
    // that the condition below distributes. Also, the Coalesce is here so
    // that when properties are omitted from TCriteria, we treat them as if
    // they always pass
    TCriteriaVal = Coalesce<TCriteria[Extract<keyof TCriteria, K>], any>,
  > = TCriteriaVal extends V | undefined ? TRelation : never
  /**
   * A {@link Relation} that meets the given {@link Where.Criteria}
   */
  export type Where<TCriteria extends Where.Criteria> = TCriteria extends any
    ? (
        | WhereHelper<TCriteria, 'singleton', true, Where.Singleton>
        | WhereHelper<TCriteria, 'singleton', false, Where.Multiton>
      ) &
        (
          | WhereHelper<
              TCriteria,
              'inheritance',
              Inheritance.IncludesAncestors,
              Where.Ancestor
            >
          | WhereHelper<
              TCriteria,
              'inheritance',
              Inheritance.IncludesDescendants,
              Where.Descendant
            >
        )
    : never
  export namespace Where {
    /**
     * Sometimes we only want to support {@link Relation} that meet
     * particular criteria. These are the relevant criteria that we may want
     * to consider. This can be used with {@link Where} to narrow down the
     * appropriate type
     */
    export interface Criteria {
      /**
       * If `true`, only {@link Relation} that correspond to a single
       * {@link models.Entity} are used. If `false`, only {@link Relation} that can
       * correspond to multiple {@link models.Entity} are used
       */
      readonly singleton?: boolean
      /**
       * Each {@link Relation} correspond to a particular
       * {@link Inheritance}. Use this to filter down by
       * {@link Inheritance}
       */
      readonly inheritance?: Inheritance
    }

    // NOTE: these are used for types (compile-time) and type-checkers (runtime)
    const ANCESTOR_RELATIONS = [
      'ancestors',
      'parent',
      'root',
    ] as const satisfies readonly Relation[]
    const MULTITON_RELATIONS = [
      'ancestors',
      'descendants',
    ] as const satisfies readonly Relation[]

    export function getTypeChecker<TCriteria extends Criteria>(
      criteria: TCriteria,
    ): TypeChecker<Where<TCriteria>>
    export function getTypeChecker(criteria: Criteria): TypeChecker<Relation> {
      const typeCheckers: TypeChecker<Relation>[] = []
      // Singleton
      switch (criteria.singleton) {
        case true:
          typeCheckers.push(Singleton.isSingleton)
          break
        case false:
          typeCheckers.push(Multiton.isMultiton)
          break
      }
      // Inheritance
      switch (criteria.inheritance) {
        case '+':
          typeCheckers.push(Ancestor.isAncestor)
          break
        case '-':
          typeCheckers.push(Descendant.isDescendant)
          break
        case '=':
          return TypeChecker.isNever
      }
      // Combined
      switch (typeCheckers.length) {
        case 0:
          // If there are no restrictions, then any Relation is acceptable
          return Relation.isRelation
        case 1:
          // If there is just one, use that
          return typeCheckers[0]
        default:
          // Otherwise, intersect them together
          return TypeChecker.Factory.intersection(...typeCheckers)
      }
    }

    /**
     * Corresponds to {@link Inheritance.Ancestors}
     */
    export type Ancestor = (typeof ANCESTOR_RELATIONS)[number]
    export namespace Ancestor {
      export const isAncestor: TypeChecker<Ancestor> =
        TypeChecker.Factory.oneOf(ANCESTOR_RELATIONS)
    }
    /**
     * Corresponds to {@link Inheritance.Descendants}
     */
    export type Descendant = Exclude<Relation, Ancestor>
    export namespace Descendant {
      export const isDescendant: TypeChecker<Descendant> =
        TypeChecker.Factory.exclude(isRelation, Ancestor.isAncestor)
    }
    /**
     * Corresponds to {@link Where.Criteria.singleton} === `false`
     */
    export type Multiton = (typeof MULTITON_RELATIONS)[number]
    export namespace Multiton {
      export const isMultiton: TypeChecker<Multiton> =
        TypeChecker.Factory.oneOf(MULTITON_RELATIONS)
    }
    /**
     * Corresponds to {@link Where.Criteria.singleton} === `true`
     */
    export type Singleton = Exclude<Relation, Multiton>
    export namespace Singleton {
      export const isSingleton: TypeChecker<Singleton> =
        TypeChecker.Factory.exclude(isRelation, Multiton.isMultiton)
    }
  }
}
